import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";

const features = [
  {
    title: 'Effortless AI-Powered Assistance',
    description: 'Plan, set up, and automate engagement,no hassle, no delays.',
    image: '/images/solutions/ai-assistant/post-event.jpg',
    bullets: [
      '24/7 Instant Response – Never miss a visitor query with real-time AI engagement.',
      'Smart Event Discovery – Recommends community & corporate events instantly based on visitor interest.',
      'Mission Hub Integration – Add within your exisiting webpage & Mission page in minutes.',
      'Seamless Event Sharing – AI Assistant auto-shares event links, registration pages.'
    ]
  },
  {
    title: 'AI-Powered Lead Capture & Intelligence',
    description: 'Convert every visitor interaction into a meaningful engagement.',
    image: '/images/solutions/ai-assistant/volunteer-management.jpg',
    bullets: [
      'Automated Lead Collection – Captures emails & visitor intent directly from chat.',
      'Instant Email Triggers – Notifies nonprofits about new leads, including chat history & visitor interest.',
      'Complete Chat History & Analysis – AI reviews and organizes insights from past conversations.'
    ]
  },
  {
    title: 'Smart Communication & Multilingual Support',
    description: 'Engage a global audience with seamless, AI-driven interactions.',
    image: '/images/solutions/ai-assistant/event-booking.jpg',
    bullets: [
      'Natural Language Interface – AI responds just like a human, beyond a standard chatbot.',
      'Multilingual Capabilities – Supports English, Spanish, French, German, Hindi, Tamil, Telugu, Mandarin, and more.',
      'Context-Aware Conversations – AI learns from past projects, reports, event listings, and nonprofit resources to provide intelligent responses.',
    ]
  },
];

export const AIAssistantPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2FCE2] to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-[#2D7D46] text-white py-20">
        <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-10" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-3xl font-bold mb-5">
            AI Impact Assistant: Your 24/7 Smart Engagement Tool
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-6">
            Engage, Guide, and Capture Leads
            </p>
            <p className="text-lg text-gray-200">
            AI Impact Assistant transforms the way nonprofits interact with visitors Whether answering event queries, sharing volunteer opportunities, or capturing leads.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 py-16">
        {features.map((feature, index) => (
          <div 
            key={feature.title}
            className={`flex flex-col md:flex-row items-center gap-12 py-16 ${
              index % 2 === 1 ? 'md:flex-row-reverse' : ''
            }`}
          >
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{feature.title}</h2>
              <p className="text-gray-600 mb-6">{feature.description}</p>
              <ul className="space-y-3">
                {feature.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1">
              <img
                src={feature.image}
                alt={feature.title}
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="bg-[#2D7D46] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Experience the Power of AI?
          </h2>
          <p className="text-xl mb-8">
            Join CauseConnect today and transform your nonprofit operations with AI.
          </p>
          <Button
            size="lg"
            className="bg-white text-[#2D7D46] hover:bg-gray-100"
            onClick={() => navigate("/signup")}
          >
            Get Started Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantPage;
